/**
 * Pure transform from a persisted Connection to TanStack form values.
 *
 * Extracted from NewConnectionPopup so it can be unit-tested in isolation
 * (otherwise the whole React tree has to mount).
 */

import type { Connection } from '../../../types/electron';
import type { ConnectionFormValues, DatabaseEngine } from './connectionFormSchema';

export function connectionToFormValues(conn: Connection): ConnectionFormValues {
  return {
    engine: (conn.engine || 'mssql') as DatabaseEngine,
    alias: conn.alias || '',
    server: conn.server || '',
    port: conn.port?.toString() || '<auto>',
    authentication: conn.authentication,
    login: conn.login || '',
    password: conn.password || '',
    database: conn.database || '',
    limitToDatabase: conn.limitToDatabase ?? false,
    entraTenantId: conn.entraTenantId || '',
    encrypt: conn.encrypt ?? true,
    trustServerCertificate: conn.trustServerCertificate ?? false,
    ssl: conn.ssl ?? false,
    readOnly: conn.readOnly ?? false,
    mcpPermissionLevel: conn.mcpPermissionLevel || 'inherit',
    color: conn.color,
    sshTunnel: conn.sshTunnel,
    // Oracle address fields (regression: editing an Oracle connection dropped
    // them, so the form showed an empty Service Name and a save lost them).
    oracleServiceName: conn.oracleServiceName,
    oracleUseSID: conn.oracleUseSID,
    oracleInitialSchema: conn.oracleInitialSchema,
    oracleConnectionStringType: conn.oracleConnectionStringType,
    oracleTnsDescriptor: conn.oracleTnsDescriptor,
    oracleTnsAlias: conn.oracleTnsAlias,
    oracleServerFromTnsTarget: conn.oracleServerFromTnsTarget,
    oracleTnsAdmin: conn.oracleTnsAdmin,
    oracleWalletLocation: conn.oracleWalletLocation,
    oracleDebugProtocol: conn.oracleDebugProtocol,
    // Azure Storage fields (same regression class as the Oracle ones above:
    // the whitelist would otherwise drop them and a save would lose them —
    // including azureResourceId, which "Open in Azure portal" reads).
    azureStorageEndpoints: conn.azureStorageEndpoints,
    azureStorageServices: conn.azureStorageServices,
    azureStorageScope: conn.azureStorageScope,
    azureStorageEmulator: conn.azureStorageEmulator,
    azureResourceId: conn.azureResourceId,
    // Browse Azure provenance (Option C C4a): 'picker' must survive an edit, or the missing-origin rule would treat a
    // picked id as user-entered afterwards.
    azureResourceIdOrigin: conn.azureResourceIdOrigin,
    azureStorageDataAccess: conn.azureStorageDataAccess,
    azureStorageNamedShares: conn.azureStorageNamedShares, // S7 §3.2
  };
}
